import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from "react"
import { useAuth } from "@/lib/auth-context"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Building2, Plus, Warehouse, Users, UserPlus, Trash2, Copy, Check, Edit2, X, Search, ExternalLink } from "lucide-react"
import { MASK_MAX_LENGTH, maskCnpj, onlyDigits } from "@/lib/br-masks"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { API_BASE } from '@/lib/api-base'

const PAIRING_POLL_MS = 3500

interface GranjaItem {
  id: string
  nome: string
  tipo_producao: string
  organization_id: string
}

interface UserVinculo {
  org_id: string | null
  org_nome: string | null
  granja_id: string
  granja_nome: string
  role_granja: string
}

interface UserItem {
  id: string
  email: string
  nome: string
  role: string
  status: string
  telegram_id: number | null
  created_at: string
  vinculos?: UserVinculo[]
}

interface TelegramPairing {
  deep_link: string
  expires_at: string
}

interface GranjaMember {
  id: string
  granja_id: string
  user_id: string
  user_nome: string
  user_email: string
  role_granja: string
  status: string
}

const ROLE_GRANJA_LABELS: Record<string, string> = {
  admin: "Admin",
  financeiro: "Financeiro",
  operador: "Operador",
}

const ROLE_GRANJA_OPTIONS = [
  { value: "operador", label: "Operador" },
  { value: "financeiro", label: "Financeiro" },
  { value: "admin", label: "Admin" },
]

function normalizeSearch(value: string): string {
  return value.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
}

const FILTER_SELECT_CLASS = "h-10 rounded-md border border-input bg-background px-3 text-sm"

interface EditingOrg {
  id: string
  nome: string
  cnpj: string
}

interface EditingGranjaMember {
  id: string
  granja_id: string
  user_id: string
  user_nome: string
  user_email: string
  role: string
}

function AdminPage() {
  const { 
    user, isLoading, apiFetch, isSuperAdmin, 
    organizations, refetchOrganizations, refetchTenants,
    selectedOrgId, selectedTenantId,
  } = useAuth()
  
  const [granjasByOrg, setGranjasByOrg] = useState<Record<string, GranjaItem[]>>({})
  const [detailOrgId, setDetailOrgId] = useState<string | null>(null)
  const [detailGranjaId, setDetailGranjaId] = useState<string | null>(null)
  const [detailGranjaOrgId, setDetailGranjaOrgId] = useState<string | null>(null)
  
  const [showNewOrg, setShowNewOrg] = useState(false)
  const [newOrgForm, setNewOrgForm] = useState({ nome: "", cnpj: "" })
  
  const [newGranjaOrgId, setNewGranjaOrgId] = useState<string | null>(null)
  const [newGranjaForm, setNewGranjaForm] = useState({ nome: "", tipo_producao: "postura" })
  
  const [users, setUsers] = useState<UserItem[]>([])
  
  const [granjaMembers, setGranjaMembers] = useState<Record<string, GranjaMember[]>>({})
  
  const [showAddMember, setShowAddMember] = useState(false)
  const [addMemberGranjaId, setAddMemberGranjaId] = useState<string | null>(null)
  const [addMemberForm, setAddMemberForm] = useState({ user_id: "", role: "operador" })
  
  const [showNewUser, setShowNewUser] = useState(false)
  const [newUserForm, setNewUserForm] = useState({
    nome: "",
    email: "",
    senha: "",
    role: "user",
    org_id: "",
    granja_id: "",
    role_granja: "operador",
  })
  
  const [activeTab, setActiveTab] = useState<"usuarios" | "estruturas">("estruturas")
  
  const [editingOrg, setEditingOrg] = useState<EditingOrg | null>(null)
  const [editingGranjaMember, setEditingGranjaMember] = useState<EditingGranjaMember | null>(null)
  const [telegramPairing, setTelegramPairing] = useState<{ user: UserItem; data: TelegramPairing } | null>(null)
  const [pairingNow, setPairingNow] = useState(Date.now())
  
  const [estruturaBusca, setEstruturaBusca] = useState("")
  const [estruturaTipo, setEstruturaTipo] = useState("")
  const [usuarioBusca, setUsuarioBusca] = useState("")
  const [usuarioRole, setUsuarioRole] = useState("")
  const [usuarioStatus, setUsuarioStatus] = useState("")
  const [usuarioVinculo, setUsuarioVinculo] = useState("")
  const [usuarioOrgId, setUsuarioOrgId] = useState("")
  
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const apiFetchRef = useRef(apiFetch)

  useEffect(() => {
    apiFetchRef.current = apiFetch
  }, [apiFetch])

  useEffect(() => {
    if (!telegramPairing) return
    const interval = window.setInterval(() => setPairingNow(Date.now()), 1000)
    return () => window.clearInterval(interval)
  }, [telegramPairing])

  useEffect(() => {
    if (!telegramPairing) return

    const expiresAt = Date.parse(telegramPairing.data.expires_at)
    const targetUserId = telegramPairing.user.id
    if (Number.isNaN(expiresAt) || Date.now() >= expiresAt) return

    let cancelled = false

    const checkLinked = async () => {
      if (cancelled || Date.now() >= expiresAt) return

      try {
        const res = await apiFetchRef.current(`${API_BASE}/api/admin/users/${targetUserId}`)
        if (!res.ok || cancelled) return
        const userData = (await res.json()) as UserItem
        if (userData.telegram_id == null) return

        cancelled = true
        setTelegramPairing(null)
        setUsers((prev) =>
          prev.map((u) =>
            u.id === targetUserId ? { ...u, telegram_id: userData.telegram_id } : u,
          ),
        )
        setError(null)
        setSuccess("Vínculo confirmado.")
        setTimeout(() => setSuccess(null), 3000)
      } catch {
        // mantém polling em erros transitórios
      }
    }

    void checkLinked()
    const interval = window.setInterval(() => {
      if (Date.now() >= expiresAt) {
        window.clearInterval(interval)
        return
      }
      void checkLinked()
    }, PAIRING_POLL_MS)

    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [telegramPairing])

  useEffect(() => {
    if (!user || !isSuperAdmin) return
    fetchGranjas()
    fetchUsers()
  }, [user, isSuperAdmin, organizations])

  const fetchGranjas = async () => {
    await Promise.all(
      organizations.map(async (org) => {
        const res = await apiFetch(`${API_BASE}/api/organizations/${org.id}/granjas`)
        if (res.ok) {
          const list = await res.json()
          setGranjasByOrg(prev => ({ ...prev, [org.id]: list }))
        }
      })
    )
  }

  const fetchUsers = async () => {
    const res = await apiFetch(`${API_BASE}/api/admin/users?limit=100`)
    if (res.ok) {
      const data = await res.json()
      setUsers(data.items || [])
    }
  }

  const fetchGranjaMembers = async (granjaId: string) => {
    const res = await apiFetch(`${API_BASE}/api/admin/granjas/${granjaId}/members`)
    if (res.ok) {
      const members = await res.json()
      setGranjaMembers(prev => ({ ...prev, [granjaId]: members }))
    }
  }

  const openGranjaDetail = (granjaId: string, orgId: string) => {
    setDetailGranjaId(granjaId)
    setDetailGranjaOrgId(orgId)
    fetchGranjaMembers(granjaId)
  }

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const res = await apiFetch(`${API_BASE}/api/admin/organizations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nome: newOrgForm.nome.trim(), cnpj: onlyDigits(newOrgForm.cnpj) || null }),
      })
      if (res.ok) {
        setShowNewOrg(false)
        setNewOrgForm({ nome: "", cnpj: "" })
        await refetchOrganizations()
        setSuccess("Organizacao criada com sucesso!")
        setTimeout(() => setSuccess(null), 3000)
      } else {
        const data = await res.json().catch(() => ({}))
        setError(data.detail || "Erro ao criar organizacao")
      }
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateOrg = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingOrg) return
    setError(null)
    setLoading(true)
    try {
      const res = await apiFetch(`${API_BASE}/api/admin/organizations/${editingOrg.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nome: editingOrg.nome.trim(), cnpj: onlyDigits(editingOrg.cnpj) || null }),
      })
      if (res.ok) {
        setEditingOrg(null)
        await refetchOrganizations()
        setSuccess("Organizacao atualizada!")
        setTimeout(() => setSuccess(null), 3000)
      } else {
        const data = await res.json().catch(() => ({}))
        setError(data.detail || "Erro ao atualizar organizacao")
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (newUserForm.role !== "super_admin" && !newUserForm.granja_id) {
      setError("Selecione a organizacao e a granja para vincular o usuario.")
      return
    }
    const granjaId = newUserForm.granja_id
    setLoading(true)
    try {
      const payload: Record<string, string | undefined> = {
        nome: newUserForm.nome.trim(),
        email: newUserForm.email.trim(),
        senha: newUserForm.senha,
        role: newUserForm.role,
      }
      if (newUserForm.role !== "super_admin") {
        payload.granja_id = newUserForm.granja_id
        payload.role_granja = newUserForm.role_granja
      }
      const res = await apiFetch(`${API_BASE}/api/admin/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
      if (res.ok) {
        setShowNewUser(false)
        setNewUserForm({
          nome: "",
          email: "",
          senha: "",
          role: "user",
          org_id: selectedOrgId || "",
          granja_id: selectedTenantId || "",
          role_granja: "operador",
        })
        fetchUsers()
        if (granjaId) fetchGranjaMembers(granjaId)
        setSuccess("Usuario criado e vinculado a granja.")
        setTimeout(() => setSuccess(null), 3000)
      } else {
        const data = await res.json().catch(() => ({}))
        setError(data.detail || "Erro ao criar usuario")
      }
    } finally {
      setLoading(false)
    }
  }

  const handleUnlinkUserTelegram = async (targetUser: UserItem) => {
    if (!window.confirm(`Desvincular o Telegram de ${targetUser.nome}?`)) return
    setError(null)
    setLoading(true)
    try {
      const res = await apiFetch(`${API_BASE}/api/admin/users/${targetUser.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ telegram_id: 0 }),
      })
      if (res.ok) {
        await fetchUsers()
        setSuccess("Telegram desvinculado.")
        setTimeout(() => setSuccess(null), 3000)
      } else {
        const data = await res.json().catch(() => ({}))
        setError(data.detail || "Erro ao desvincular Telegram.")
      }
    } catch {
      setError("Erro de conexão.")
    } finally {
      setLoading(false)
    }
  }

  const handleCreateUserTelegramPairing = async (targetUser: UserItem) => {
    setError(null)
    setLoading(true)
    try {
      const res = await apiFetch(`${API_BASE}/api/admin/users/${targetUser.id}/telegram/pairings`, { method: "POST" })
      if (res.ok) {
        setTelegramPairing({ user: targetUser, data: await res.json() as TelegramPairing })
        setPairingNow(Date.now())
      } else if (res.status === 403) {
        setError("Não é possível gerar link para usuário super admin ou inativo.")
      } else if (res.status === 409) {
        setError("Este usuário já possui Telegram vinculado.")
      } else {
        const data = await res.json().catch(() => ({})) as { detail?: string }
        setError(data.detail || "Não foi possível gerar o link de vinculação.")
      }
    } catch {
      setError("Erro de conexão.")
    } finally {
      setLoading(false)
    }
  }

  const handleCopyTelegramPairing = async () => {
    if (!telegramPairing) return
    try {
      await navigator.clipboard.writeText(telegramPairing.data.deep_link)
      setSuccess("Link de vinculação copiado.")
      setTimeout(() => setSuccess(null), 3000)
    } catch {
      setError("Não foi possível copiar o link. Copie-o manualmente.")
    }
  }

  const pairingSeconds = telegramPairing
    ? Math.max(0, Math.ceil((Date.parse(telegramPairing.data.expires_at) - pairingNow) / 1000))
    : 0

  const handleCreateGranja = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newGranjaOrgId) return
    setError(null)
    setLoading(true)
    try {
      const res = await apiFetch(`${API_BASE}/api/organizations/${newGranjaOrgId}/granjas`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nome: newGranjaForm.nome.trim(), tipo_producao: newGranjaForm.tipo_producao || "postura" }),
      })
      if (res.ok) {
        setNewGranjaOrgId(null)
        setNewGranjaForm({ nome: "", tipo_producao: "postura" })
        await fetchGranjas()
        await refetchTenants()
        setSuccess("Granja criada com sucesso!")
        setTimeout(() => setSuccess(null), 3000)
      } else {
        const data = await res.json().catch(() => ({}))
        setError(data.detail || "Erro ao criar granja")
      }
    } finally {
      setLoading(false)
    }
  }

  const handleAddGranjaMember = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!addMemberGranjaId) return
    setError(null)
    setLoading(true)
    try {
      const res = await apiFetch(`${API_BASE}/api/admin/granjas/${addMemberGranjaId}/members`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: addMemberForm.user_id, role_granja: addMemberForm.role }),
      })
      if (res.ok) {
        setShowAddMember(false)
        setAddMemberForm({ user_id: "", role: "operador" })
        fetchGranjaMembers(addMemberGranjaId)
        setSuccess("Membro adicionado a granja!")
        setTimeout(() => setSuccess(null), 3000)
      } else {
        const data = await res.json().catch(() => ({}))
        setError(data.detail || "Erro ao adicionar membro")
      }
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateGranjaMember = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingGranjaMember) return
    setError(null)
    setLoading(true)
    try {
      const res = await apiFetch(`${API_BASE}/api/admin/granjas/${editingGranjaMember.granja_id}/members/${editingGranjaMember.user_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role_granja: editingGranjaMember.role }),
      })
      if (res.ok) {
        setEditingGranjaMember(null)
        fetchGranjaMembers(editingGranjaMember.granja_id)
        setSuccess("Permissao atualizada!")
        setTimeout(() => setSuccess(null), 3000)
      } else {
        const data = await res.json().catch(() => ({}))
        setError(data.detail || "Erro ao atualizar permissao")
      }
    } finally {
      setLoading(false)
    }
  }

  const handleReplicarPermissao = async (userId: string, role: string, orgId: string) => {
    if (!confirm("Replicar esta permissao para TODAS as granjas desta organizacao?")) return
    
    const granjas = granjasByOrg[orgId] || []
    setError(null)
    setLoading(true)

    const resultados = await Promise.all(
      granjas.map(async (granja) => {
        const res = await apiFetch(`${API_BASE}/api/admin/granjas/${granja.id}/members`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId, role_granja: role }),
        })
        return res.ok
      })
    )
    const successCount = resultados.filter(Boolean).length

    setLoading(false)
    setSuccess(`Permissao replicada para ${successCount} granja(s)!`)
    setTimeout(() => setSuccess(null), 3000)
    
    if (detailGranjaId) fetchGranjaMembers(detailGranjaId)
  }

  const estruturaFiltroAtivo = Boolean(estruturaBusca.trim() || estruturaTipo)
  const estruturasFiltradas = useMemo(() => {
    const q = normalizeSearch(estruturaBusca.trim())
    const qDigits = onlyDigits(estruturaBusca)
    return organizations.flatMap((org) => {
      const granjas = granjasByOrg[org.id] || []
      const orgHay = `${normalizeSearch(org.nome)} ${normalizeSearch(org.cnpj || "")} ${onlyDigits(org.cnpj || "")}`
      const orgMatches = Boolean(q) && (
        orgHay.includes(q) || (qDigits.length >= 2 && onlyDigits(org.cnpj || "").includes(qDigits))
      )
      const byTipo = (g: GranjaItem) => !estruturaTipo || g.tipo_producao === estruturaTipo

      if (!q && !estruturaTipo) return [{ org, granjas }]
      if (orgMatches) return [{ org, granjas: granjas.filter(byTipo) }]

      const granjasFiltradas = granjas.filter((g) => byTipo(g) && (!q || normalizeSearch(g.nome).includes(q)))
      if (granjasFiltradas.length === 0) return []
      return [{ org, granjas: granjasFiltradas }]
    })
  }, [organizations, granjasByOrg, estruturaBusca, estruturaTipo])

  const usuariosFiltrados = useMemo(() => {
    const q = normalizeSearch(usuarioBusca.trim())
    return users.filter((u) => {
      if (usuarioRole && u.role !== usuarioRole) return false
      if (usuarioStatus && u.status !== usuarioStatus) return false

      const vinculos = u.vinculos || []
      if (usuarioVinculo === "com_granja" && (u.role === "super_admin" || vinculos.length === 0)) return false
      if (usuarioVinculo === "sem_granja" && (u.role === "super_admin" || vinculos.length > 0)) return false
      if (usuarioVinculo === "global" && u.role !== "super_admin") return false
      if (usuarioOrgId && !vinculos.some((v) => v.org_id === usuarioOrgId)) return false

      if (q) {
        const hay = [u.nome, u.email, ...vinculos.flatMap((v) => [v.granja_nome, v.org_nome || ""])]
          .map(normalizeSearch)
          .join(" ")
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [users, usuarioBusca, usuarioRole, usuarioStatus, usuarioVinculo, usuarioOrgId])

  const handleRemoveGranjaMember = async (granjaId: string, userId: string) => {
    if (!confirm("Remover este membro da granja?")) return
    await apiFetch(`${API_BASE}/api/admin/granjas/${granjaId}/members/${userId}`, { method: "DELETE" })
    fetchGranjaMembers(granjaId)
  }

  const detailOrg = detailOrgId ? organizations.find(o => o.id === detailOrgId) : null
  const granjasDoOrgDetalhe = detailOrgId ? (granjasByOrg[detailOrgId] || []) : []
  const detailGranja = detailGranjaId
    ? Object.values(granjasByOrg).flat().find(g => g.id === detailGranjaId) ?? null
    : null

  if (isLoading || !user) return <div className="p-6"><p>Carregando...</p></div>
  if (!isSuperAdmin) return null

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-6xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold">Super Admin</h1>
        <p className="text-muted-foreground text-sm">Gerencie organizações, usuários e permissões</p>
      </div>

      {error && <div className="p-3 bg-destructive/10 text-destructive rounded text-sm">{error}</div>}
      {success && <div className="p-3 bg-green-50 dark:bg-green-950/20 text-green-800 dark:text-green-200 rounded text-sm flex items-center gap-2"><Check className="h-4 w-4" />{success}</div>}

      <div className="flex gap-2 border-b pb-2">
        <Button variant={activeTab === "estruturas" ? "default" : "ghost"} onClick={() => setActiveTab("estruturas")}>
          <Building2 className="h-4 w-4 mr-2" />Estruturas
        </Button>
        <Button variant={activeTab === "usuarios" ? "default" : "ghost"} onClick={() => setActiveTab("usuarios")}>
          <Users className="h-4 w-4 mr-2" />Usuários
        </Button>
      </div>

      {activeTab === "estruturas" && (
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="flex min-w-0 items-center gap-2">
                <Building2 className="h-5 w-5" />
                Organizações e Granjas
              </CardTitle>
              <Button className="self-start sm:self-auto" onClick={() => { setShowNewOrg(true); setError(null) }}>
                <Plus className="h-4 w-4 mr-2" />Nova Organização
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex flex-col gap-2 sm:flex-row">
              <div className="relative min-w-0 flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-8"
                  placeholder="Buscar por organizacao, CNPJ ou granja..."
                  value={estruturaBusca}
                  onChange={(e) => setEstruturaBusca(e.target.value)}
                />
              </div>
              <select
                className={`${FILTER_SELECT_CLASS} w-full sm:w-auto`}
                value={estruturaTipo}
                onChange={(e) => setEstruturaTipo(e.target.value)}
                aria-label="Filtrar por tipo de producao"
              >
                <option value="">Todos os tipos</option>
                <option value="postura">Postura</option>
                <option value="corte">Corte</option>
                <option value="misto">Misto</option>
              </select>
            </div>
            {showNewOrg && (
              <Card className="mb-4 bg-muted/30">
                <CardContent className="pt-4">
                  <form onSubmit={handleCreateOrg} className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
                    <div className="w-full min-w-0 sm:flex-1">
                      <Label>Nome *</Label>
                      <Input value={newOrgForm.nome} onChange={e => setNewOrgForm(f => ({ ...f, nome: e.target.value }))} required />
                    </div>
                    <div className="w-full sm:w-48">
                      <Label>CNPJ</Label>
                      <Input value={newOrgForm.cnpj} onChange={e => setNewOrgForm(f => ({ ...f, cnpj: maskCnpj(e.target.value) }))} placeholder="00.000.000/0000-00" inputMode="numeric" maxLength={MASK_MAX_LENGTH.cnpj} />
                    </div>
                    <Button className="w-full sm:w-auto" type="submit" disabled={loading}>Criar</Button>
                    <Button className="w-full sm:w-auto" type="button" variant="outline" onClick={() => setShowNewOrg(false)}>Cancelar</Button>
                  </form>
                </CardContent>
              </Card>
            )}
            
            <div className="space-y-2">
              {estruturasFiltradas.map(({ org, granjas }) => (
                <div key={org.id} className="border rounded-lg">
                  <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0 flex-1 flex items-center gap-2">
                      <Building2 className="h-5 w-5 shrink-0 text-blue-500" />
                      <span className="truncate font-medium">{org.nome}</span>
                      {org.cnpj && <span className="truncate text-sm text-muted-foreground">({org.cnpj})</span>}
                      <Badge variant="secondary">{granjas.length} granja(s)</Badge>
                    </div>
                    <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDetailOrgId(org.id)}
                      >
                        Ver granjas
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={`Editar organização ${org.nome}`}
                        onClick={() => setEditingOrg({ id: org.id, nome: org.nome, cnpj: org.cnpj || "" })}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setNewGranjaOrgId(org.id); setNewGranjaForm({ nome: "", tipo_producao: "postura" }); setError(null) }}>
                        <Plus className="h-4 w-4 mr-1" />Granja
                      </Button>
                    </div>
                  </div>
                  {editingOrg?.id === org.id && (
                    <div className="border-t p-4 bg-muted/30">
                      <form onSubmit={handleUpdateOrg} className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
                        <div className="w-full min-w-0 sm:flex-1">
                          <Label>Nome</Label>
                          <Input value={editingOrg.nome} onChange={e => setEditingOrg(prev => prev ? { ...prev, nome: e.target.value } : null)} required />
                        </div>
                        <div className="w-full sm:w-48">
                          <Label>CNPJ</Label>
                          <Input value={editingOrg.cnpj} onChange={e => setEditingOrg(prev => prev ? { ...prev, cnpj: maskCnpj(e.target.value) } : null)} placeholder="00.000.000/0000-00" inputMode="numeric" maxLength={MASK_MAX_LENGTH.cnpj} />
                        </div>
                        <Button className="w-full sm:w-auto" type="submit" disabled={loading}>Salvar</Button>
                        <Button className="w-full sm:w-auto" type="button" variant="outline" onClick={() => setEditingOrg(null)}>Cancelar</Button>
                      </form>
                    </div>
                  )}
                  {newGranjaOrgId === org.id && (
                    <div className="border-t p-4 bg-muted/30">
                      <form onSubmit={handleCreateGranja} className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
                        <div className="w-full min-w-0 sm:flex-1">
                          <Label>Nome da Granja *</Label>
                          <Input value={newGranjaForm.nome} onChange={e => setNewGranjaForm(f => ({ ...f, nome: e.target.value }))} required />
                        </div>
                        <div className="w-full sm:w-40">
                          <Label>Tipo</Label>
                          <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2" value={newGranjaForm.tipo_producao} onChange={e => setNewGranjaForm(f => ({ ...f, tipo_producao: e.target.value }))}>
                            <option value="postura">Postura</option>
                            <option value="corte">Corte</option>
                            <option value="misto">Misto</option>
                          </select>
                        </div>
                        <Button className="w-full sm:w-auto" type="submit" disabled={loading}>Criar</Button>
                        <Button className="w-full sm:w-auto" type="button" variant="outline" onClick={() => setNewGranjaOrgId(null)}>Cancelar</Button>
                      </form>
                    </div>
                  )}
                </div>
              ))}
              {estruturasFiltradas.length === 0 && organizations.length > 0 && (
                <p className="text-muted-foreground text-center py-4">Nenhuma estrutura encontrada com os filtros atuais.</p>
              )}
              {organizations.length === 0 && !showNewOrg && (
                <p className="text-muted-foreground text-center py-4">Nenhuma organizacao cadastrada.</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === "usuarios" && (
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Usuários do Sistema
              </CardTitle>
              <Button className="self-start sm:self-auto" onClick={() => {
                setShowNewUser(true)
                setError(null)
                setNewUserForm(f => ({
                  ...f,
                  org_id: f.org_id || selectedOrgId || "",
                  granja_id: f.granja_id || selectedTenantId || "",
                }))
              }}>
                <Plus className="h-4 w-4 mr-2" />Novo Usuário
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex flex-col gap-2 sm:flex-row">
              <div className="relative min-w-0 flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-8"
                  placeholder="Buscar por nome, e-mail, granja ou organizacao..."
                  value={usuarioBusca}
                  onChange={(e) => setUsuarioBusca(e.target.value)}
                />
              </div>
              <select
                className={FILTER_SELECT_CLASS}
                value={usuarioRole}
                onChange={(e) => setUsuarioRole(e.target.value)}
                aria-label="Filtrar por role"
              >
                <option value="">Todas as roles</option>
                <option value="user">Usuario</option>
                <option value="super_admin">Super Admin</option>
              </select>
              <select
                className={FILTER_SELECT_CLASS}
                value={usuarioStatus}
                onChange={(e) => setUsuarioStatus(e.target.value)}
                aria-label="Filtrar por status"
              >
                <option value="">Todos os status</option>
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
              </select>
              <select
                className={FILTER_SELECT_CLASS}
                value={usuarioVinculo}
                onChange={(e) => setUsuarioVinculo(e.target.value)}
                aria-label="Filtrar por vinculo"
              >
                <option value="">Todos os vinculos</option>
                <option value="com_granja">Com granja</option>
                <option value="sem_granja">Sem granja</option>
                <option value="global">Acesso global</option>
              </select>
              <select
                className={FILTER_SELECT_CLASS}
                value={usuarioOrgId}
                onChange={(e) => setUsuarioOrgId(e.target.value)}
                aria-label="Filtrar por organizacao"
              >
                <option value="">Todas as organizacoes</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>{org.nome}</option>
                ))}
              </select>
            </div>
            
            {users.length === 0 ? (
              <p className="text-muted-foreground">Nenhum usuario cadastrado.</p>
            ) : usuariosFiltrados.length === 0 ? (
              <p className="text-muted-foreground">Nenhum usuario encontrado com os filtros atuais.</p>
            ) : (
              <>
              <p className="text-xs text-muted-foreground mb-3">
                Usuario comum precisa de granja para acessar o sistema. Super admin tem acesso global. Vinculos existentes tambem podem ser gerenciados na aba Estruturas.
              </p>
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Nome</th>
                    <th className="text-left py-2">Email</th>
                    <th className="text-left py-2">Vinculos</th>
                    <th className="text-left py-2">Role</th>
                    <th className="text-left py-2">Telegram</th>
                    <th className="text-left py-2">Status</th>
                    <th className="text-left py-2">Acoes</th>
                  </tr>
                </thead>
                <tbody>
                  {usuariosFiltrados.map(u => (
                    <tr key={u.id} className="border-b">
                      <td className="py-2">{u.nome}</td>
                      <td className="py-2">{u.email}</td>
                      <td className="py-2">
                        {u.role === "super_admin" ? (
                          <span className="text-muted-foreground">Acesso global</span>
                        ) : (u.vinculos && u.vinculos.length > 0) ? (
                          <div className="flex flex-col gap-1">
                            {u.vinculos.map(v => (
                              <span key={`${v.granja_id}-${v.role_granja}`}>
                                <span className="font-medium">{v.granja_nome}</span>
                                {v.org_nome && <span className="text-muted-foreground"> · {v.org_nome}</span>}
                                <Badge variant="secondary" className="ml-2">{ROLE_GRANJA_LABELS[v.role_granja] || v.role_granja}</Badge>
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-amber-700">Sem granja</span>
                        )}
                      </td>
                      <td className="py-2"><Badge variant="secondary">{u.role}</Badge></td>
                      <td className="py-2">
                        {u.telegram_id ? (
                          <Badge className="bg-green-500">Vinculado</Badge>
                        ) : (
                          <span className="text-muted-foreground">Não vinculado</span>
                        )}
                      </td>
                      <td className="py-2"><Badge variant={u.status === "ativo" ? "default" : "secondary"}>{u.status}</Badge></td>
                      <td className="py-2">
                        {u.telegram_id ? (
                          <Button variant="ghost" size="sm" title="Desvincular Telegram" disabled={loading} onClick={() => handleUnlinkUserTelegram(u)}><Trash2 className="h-3 w-3" /></Button>
                        ) : (
                          <Button variant="outline" size="sm" disabled={loading} onClick={() => handleCreateUserTelegramPairing(u)}>Gerar link Telegram</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog
        open={showNewUser}
        onOpenChange={(open) => {
          if (!open) setShowNewUser(false)
        }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto gap-0 p-0">
          <DialogTitle className="sr-only">Novo Usuario</DialogTitle>
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold pr-10">Novo Usuario</h2>
            <form onSubmit={handleCreateUser} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div>
                <Label>Nome *</Label>
                <Input value={newUserForm.nome} onChange={e => setNewUserForm(f => ({ ...f, nome: e.target.value }))} required />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={newUserForm.email} onChange={e => setNewUserForm(f => ({ ...f, email: e.target.value }))} required />
              </div>
              <div>
                <Label>Senha *</Label>
                <Input type="password" value={newUserForm.senha} onChange={e => setNewUserForm(f => ({ ...f, senha: e.target.value }))} required />
              </div>
              <div>
                <Label>Role</Label>
                <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2" value={newUserForm.role} onChange={e => setNewUserForm(f => ({ ...f, role: e.target.value, granja_id: e.target.value === "super_admin" ? "" : f.granja_id }))}>
                  <option value="user">Usuario</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>
              {newUserForm.role !== "super_admin" && (
                <>
                  <div>
                    <Label>Organizacao *</Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2"
                      value={newUserForm.org_id}
                      onChange={e => setNewUserForm(f => ({ ...f, org_id: e.target.value, granja_id: "" }))}
                      required
                    >
                      <option value="">Selecione...</option>
                      {organizations.map(org => (
                        <option key={org.id} value={org.id}>{org.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Granja *</Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2"
                      value={newUserForm.granja_id}
                      onChange={e => setNewUserForm(f => ({ ...f, granja_id: e.target.value }))}
                      required
                    >
                      <option value="">Selecione...</option>
                      {(granjasByOrg[newUserForm.org_id] || []).map(g => (
                        <option key={g.id} value={g.id}>{g.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Papel na granja</Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2"
                      value={newUserForm.role_granja}
                      onChange={e => setNewUserForm(f => ({ ...f, role_granja: e.target.value }))}
                    >
                      {ROLE_GRANJA_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              <div className="flex gap-2 md:col-span-3">
                <Button type="submit" disabled={loading}>Criar</Button>
                <Button type="button" variant="outline" onClick={() => setShowNewUser(false)}>Cancelar</Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detailOrg} onOpenChange={(open) => { if (!open) setDetailOrgId(null) }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto gap-0 p-0">
          <DialogTitle className="sr-only">
            {detailOrg ? `Granjas de ${detailOrg.nome}` : "Granjas da organizacao"}
          </DialogTitle>
          {detailOrg && (
            <div className="p-6 space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3 pr-10">
                <div>
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-blue-500" />
                    {detailOrg.nome}
                  </h2>
                  {detailOrg.cnpj && <p className="text-sm text-muted-foreground">{detailOrg.cnpj}</p>}
                </div>
                <Button size="sm" variant="outline" onClick={() => { setNewGranjaOrgId(detailOrg.id); setNewGranjaForm({ nome: "", tipo_producao: "postura" }); setDetailOrgId(null); setError(null) }}>
                  <Plus className="h-4 w-4 mr-1" />Nova granja
                </Button>
              </div>

              <div className="space-y-2">
                <h4 className="text-sm font-medium text-muted-foreground">Granjas</h4>
                {granjasDoOrgDetalhe.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {estruturaFiltroAtivo ? "Nenhuma granja corresponde aos filtros." : "Nenhuma granja cadastrada."}
                  </p>
                ) : (
                  granjasDoOrgDetalhe.map(g => (
                    <div key={g.id} className="flex items-center justify-between gap-3 rounded-lg border p-3">
                      <div className="flex min-w-0 items-center gap-2">
                        <Warehouse className="h-4 w-4 shrink-0 text-amber-500" />
                        <span className="font-medium">{g.nome}</span>
                        <Badge variant="outline">{g.tipo_producao}</Badge>
                        {(granjaMembers[g.id] || []).length > 0 && (
                          <Badge variant="secondary">{granjaMembers[g.id].length} membros</Badge>
                        )}
                      </div>
                      <Button size="sm" variant="outline" onClick={() => openGranjaDetail(g.id, detailOrg.id)}>
                        Ver membros
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!detailGranja} onOpenChange={(open) => { if (!open) { setDetailGranjaId(null); setDetailGranjaOrgId(null); setShowAddMember(false) } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] w-[calc(100vw-2rem)] gap-0 overflow-hidden p-0 sm:w-full">
          <DialogTitle className="sr-only">
            {detailGranja ? `Membros da granja ${detailGranja.nome}` : "Membros da granja"}
          </DialogTitle>
          {detailGranja && detailGranjaId && (
            <div className="max-h-[90vh] overflow-y-auto overflow-x-hidden">
            <div className="min-w-0 max-w-full space-y-4 p-6">
              <div className="flex flex-wrap items-start justify-between gap-3 pr-10">
                <div>
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Warehouse className="h-5 w-5 text-amber-500" />
                    {detailGranja.nome}
                  </h2>
                  <Badge variant="outline">{detailGranja.tipo_producao}</Badge>
                </div>
                <Button size="sm" variant="outline" onClick={() => { setShowAddMember(true); setAddMemberGranjaId(detailGranjaId); setAddMemberForm({ user_id: "", role: "operador" }); setError(null) }}>
                  <UserPlus className="h-4 w-4 mr-1" />Adicionar
                </Button>
              </div>

              {showAddMember && addMemberGranjaId === detailGranjaId && (
                <form onSubmit={handleAddGranjaMember} className="flex min-w-0 max-w-full flex-col gap-2 rounded bg-muted/30 p-3 sm:flex-row sm:flex-wrap sm:items-end">
                  <div className="w-full min-w-0 sm:flex-1">
                    <Label>Usuario</Label>
                    <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2" value={addMemberForm.user_id} onChange={e => setAddMemberForm(f => ({ ...f, user_id: e.target.value }))}>
                      <option value="">Selecione...</option>
                      {users.map(u => <option key={u.id} value={u.id}>{u.nome} ({u.email})</option>)}
                    </select>
                  </div>
                  <div className="w-full sm:w-32">
                    <Label>Role</Label>
                    <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2" value={addMemberForm.role} onChange={e => setAddMemberForm(f => ({ ...f, role: e.target.value }))}>
                      {ROLE_GRANJA_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  </div>
                  <Button className="w-full sm:w-auto" type="submit" disabled={loading}>Adicionar</Button>
                  <Button className="w-full sm:w-auto" type="button" variant="outline" onClick={() => setShowAddMember(false)}>Cancelar</Button>
                </form>
              )}

              {(granjaMembers[detailGranjaId] || []).length === 0 ? (
                <p className="text-muted-foreground text-sm">Nenhum membro.</p>
              ) : (
                <div className="min-w-0 max-w-full overflow-x-auto">
                  <table className="w-full min-w-[18rem] text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-1">Nome</th>
                        <th className="text-left py-1">Email</th>
                        <th className="text-left py-1">Role</th>
                        <th className="text-left py-1">Acoes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {granjaMembers[detailGranjaId].map(m => (
                        <tr key={m.id} className="border-b">
                          <td className="max-w-[7rem] truncate py-1 sm:max-w-none sm:whitespace-normal">{m.user_nome}</td>
                          <td className="max-w-[8rem] truncate py-1 sm:max-w-none sm:whitespace-normal">{m.user_email}</td>
                          <td className="py-1">
                            {editingGranjaMember?.granja_id === detailGranjaId && editingGranjaMember?.user_id === m.user_id ? (
                              <form onSubmit={handleUpdateGranjaMember} className="flex flex-wrap gap-1">
                                <select
                                  className="h-7 rounded border px-1 text-xs"
                                  value={editingGranjaMember.role}
                                  onChange={e => setEditingGranjaMember(em => em ? { ...em, role: e.target.value } : null)}
                                >
                                  {ROLE_GRANJA_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                </select>
                                <Button type="submit" size="sm" variant="ghost" className="h-7 w-7 p-0" aria-label="Salvar vínculo"><Check className="h-3 w-3" /></Button>
                                <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" aria-label="Cancelar edição do vínculo" onClick={() => setEditingGranjaMember(null)}><X className="h-3 w-3" /></Button>
                              </form>
                            ) : (
                              <Badge variant="secondary">{ROLE_GRANJA_LABELS[m.role_granja] || m.role_granja}</Badge>
                            )}
                          </td>
                          <td className="py-1">
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" title="Editar" onClick={() => setEditingGranjaMember({ id: m.id, granja_id: detailGranjaId, user_id: m.user_id, user_nome: m.user_nome, user_email: m.user_email, role: m.role_granja })}>
                                <Edit2 className="h-3 w-3" />
                              </Button>
                              {detailGranjaOrgId && (
                                <Button variant="ghost" size="sm" title="Replicar para todas" onClick={() => handleReplicarPermissao(m.user_id, m.role_granja, detailGranjaOrgId)}>
                                  <Copy className="h-3 w-3" />
                                </Button>
                              )}
                              <Button variant="ghost" size="sm" onClick={() => handleRemoveGranjaMember(detailGranjaId, m.user_id)}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!telegramPairing} onOpenChange={(open) => { if (!open) setTelegramPairing(null) }}>
        <DialogContent className="max-w-xl gap-0 p-0" aria-labelledby="telegram-pairing-title">
          <DialogTitle id="telegram-pairing-title" className="sr-only">Link de vinculação Telegram</DialogTitle>
          {telegramPairing && (
            <Card className="border-0 shadow-none">
              <CardHeader>
                <CardTitle>Link de vinculação Telegram</CardTitle>
                <p className="text-sm text-muted-foreground">Envie este link para {telegramPairing.user.nome}. Somente a pessoa que abrir o link no próprio Telegram poderá confirmar o vínculo.</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-amber-700">Válido por {Math.floor(pairingSeconds / 60)}:{String(pairingSeconds % 60).padStart(2, "0")}.</p>
                <div className="flex gap-2">
                  <Input readOnly value={telegramPairing.data.deep_link} aria-label="Link de vinculação Telegram" />
                  <Button type="button" variant="outline" size="icon" onClick={handleCopyTelegramPairing} aria-label="Copiar link de vinculação"><Copy className="h-4 w-4" /></Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button asChild><a href={telegramPairing.data.deep_link} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4 mr-2" />Abrir no Telegram</a></Button>
                  <Button variant="outline" onClick={() => setTelegramPairing(null)}>Fechar</Button>
                </div>
              </CardContent>
            </Card>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/admin')({
  component: AdminPage,
})
